import { useLayoutEffect, useRef, useState } from "react";
import {
  clearNativeGlassMotionRect,
  setNativeGlassMotionRect,
} from "@/desktop/native-glass-motion";
import {
  GLASS_FORCE_ATTR,
  SURFACE_ROLE_ATTR,
} from "@/desktop/native-glass-collect";
import { requestNativeGlassRefresh } from "@/desktop/native-glass-settings";
import { prefersReducedMotion } from "@/lib/reveal-motion";

const COMPACT_HEIGHT = 46;
const START_WAIT_MS = 80;
const refresh = () => requestNativeGlassRefresh({ trackTransition: false });

/** Computed layout height is fractional and excludes the CSS entry scale.
 * Round outward on the collector's half-pixel grid so the clip never cuts
 * the bottom edge or keeps an otherwise settled native rect overridden. */
function naturalCardHeight(card: HTMLElement): number {
  const style = getComputedStyle(card);
  const usedHeight = Number.parseFloat(style.height);
  const pixels = (value: string) => Number.parseFloat(value) || 0;
  const extras =
    style.boxSizing === "border-box"
      ? 0
      : pixels(style.paddingTop) +
        pixels(style.paddingBottom) +
        pixels(style.borderTopWidth) +
        pixels(style.borderBottomWidth);
  const height = Number.isFinite(usedHeight)
    ? usedHeight + extras
    : Math.max(card.offsetHeight, card.getBoundingClientRect().height);
  return Math.ceil(height * 2) / 2;
}

/** The card's contents retain their natural size while the actual clip box
 * grows. Give native the visible box too, rather than a full-size plate under
 * a clipped web card. Collection and ResizeObserver coalesce once per frame. */
export function useToastCardResize(expanded: boolean, itemKey: string) {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!expanded) {
      setHeight(null);
      return;
    }
    const wrapper = ref.current;
    const card = wrapper?.firstElementChild;
    if (!wrapper || !(card instanceof HTMLElement)) return;
    let active = true;
    let started = false;
    let clipping = true;
    let innerFrame = 0;
    const measure = () => {
      if (active) setHeight(naturalCardHeight(card));
    };
    const syncPlate = (initialHeight?: number) => {
      if (!active) return;
      const forcedCss =
        card
          .closest(`[${GLASS_FORCE_ATTR}]`)
          ?.getAttribute(GLASS_FORCE_ATTR) === "css";
      if (forcedCss || !card.hasAttribute(SURFACE_ROLE_ATTR)) {
        clipping = false;
        clearNativeGlassMotionRect(card);
        return;
      }
      const bounds = card.getBoundingClientRect();
      const clip = wrapper.getBoundingClientRect();
      const clipBottom =
        initialHeight === undefined
          ? clip.bottom
          : Math.min(clip.bottom, clip.top + initialHeight);
      // Once the clip is gone, let collection read live DOM positions again
      // so dragging or moving the stack cannot reuse a stale viewport rect.
      if (clipBottom >= bounds.bottom && clip.top <= bounds.top) {
        clipping = false;
        clearNativeGlassMotionRect(card);
        refresh();
        return;
      }
      clipping = true;
      const x = Math.max(bounds.left, clip.left);
      const y = Math.max(bounds.top, clip.top);
      setNativeGlassMotionRect(card, {
        x,
        y,
        width: Math.max(0, Math.min(bounds.right, clip.right) - x),
        height: Math.max(0, Math.min(bounds.bottom, clipBottom) - y),
      });
      refresh();
    };
    const begin = () => {
      if (!active || started) return;
      started = true;
      measure();
      syncPlate();
    };
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver((entries) => {
            if (started && entries.some((entry) => entry.target === card))
              measure();
            syncPlate();
          });
    observer?.observe(card);
    observer?.observe(wrapper);
    // A column drag, scroll or neighbor insertion can change position without
    // resizing this card. Resample only while its clip overrides native rects.
    const followPosition = () => {
      if (clipping) syncPlate();
    };
    const stack =
      wrapper.closest('[data-testid="toast-stack"]') ?? wrapper.parentElement;
    const movementObserver =
      typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver(followPosition);
    if (stack)
      movementObserver?.observe(stack, {
        attributes: true,
        attributeFilter: ["style", "class"],
        childList: true,
        subtree: true,
      });
    window.addEventListener("scroll", followPosition, true);
    window.addEventListener("resize", followPosition);
    const reduced = prefersReducedMotion();
    setHeight(reduced ? naturalCardHeight(card) : COMPACT_HEIGHT);
    syncPlate(reduced ? undefined : COMPACT_HEIGHT);
    if (reduced) begin();
    // Inactive webviews can pause rAF. Keep the expansion timer-backed.
    const timer = window.setTimeout(begin, START_WAIT_MS);
    const frame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(begin);
    });
    return () => {
      active = false;
      window.clearTimeout(timer);
      cancelAnimationFrame(frame);
      cancelAnimationFrame(innerFrame);
      observer?.disconnect();
      movementObserver?.disconnect();
      window.removeEventListener("scroll", followPosition, true);
      window.removeEventListener("resize", followPosition);
      clearNativeGlassMotionRect(card);
      refresh();
    };
  }, [expanded, itemKey]);
  return { ref, height };
}
